// client/src/components/PlanFeatures.tsx

import { PLANS, PlanType, PLAN_NAMES, PLAN_PRICES } from "@shared/plans";
import { Check, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";

interface PlanFeaturesProps {
  currentPlan: PlanType;
  onUpgrade?: (plan: PlanType) => void;
}

export function PlanFeatures({ currentPlan, onUpgrade }: PlanFeaturesProps) {
  const plans: PlanType[] = ["basic", "professional", "enterprise"];

  const getPlanIcon = (plan: PlanType) => {
    switch (plan) {
      case "basic":
        return "🌱";
      case "professional":
        return "💼";
      case "enterprise":
        return "🏢";
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {plans.map((plan) => {
        const limits = PLANS[plan];
        const isCurrentPlan = plan === currentPlan;

        return (
          <Card
            key={plan}
            className={`relative flex flex-col ${
              isCurrentPlan
                ? "border-purple-500 shadow-lg ring-2 ring-purple-200"
                : "border-gray-200"
            }`}
          >
            {isCurrentPlan && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white text-xs font-medium px-3 py-1 rounded-full shadow-md">
                  Plan actual
                </span>
              </div>
            )}

            <CardHeader className="text-center pb-2">
              <div className="text-4xl mb-2">{getPlanIcon(plan)}</div>
              <CardTitle className="text-2xl font-bold">
                {PLAN_NAMES[plan]}
              </CardTitle>
              <CardDescription>
                {plan === "basic" &&
                  "Para negocios pequeños que están comenzando"}
                {plan === "professional" && "Para negocios en crecimiento"}
                {plan === "enterprise" && "Para cadenas y grandes operaciones"}
              </CardDescription>
            </CardHeader>

            <CardContent className="flex-1">
              {/* Precio */}
              <div className="text-center mb-6">
                <span className="text-3xl font-bold">
                  {plan === "basic"
                    ? "Gratis"
                    : `$${PLAN_PRICES[plan].monthly}`}
                </span>
                {plan !== "basic" && (
                  <span className="text-muted-foreground">/mes</span>
                )}
              </div>

              {/* Límites principales */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Tiendas máximas</span>
                  <span className="font-medium">
                    {limits.maxShops === Infinity
                      ? "∞ Ilimitadas"
                      : limits.maxShops}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">
                    Productos máximos
                  </span>
                  <span className="font-medium">
                    {limits.maxProducts === Infinity
                      ? "∞ Ilimitados"
                      : limits.maxProducts}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">
                    Usuarios por tienda
                  </span>
                  <span className="font-medium">
                    {limits.maxUsersPerShop === Infinity
                      ? "∞ Ilimitados"
                      : limits.maxUsersPerShop}
                  </span>
                </div>
              </div>

              {/* ===================================================== */}
              {/* CARACTERÍSTICAS DEL POS - NUEVA SECCIÓN */}
              {/* ===================================================== */}
              <div className="space-y-3 pt-4 border-t">
                <p className="font-medium text-sm mb-3">
                  Punto de Venta (POS):
                </p>

                {/* Apertura/cierre de turno */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.posFeatures.shiftManagement ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span
                    className={
                      !limits.posFeatures.shiftManagement
                        ? "text-muted-foreground"
                        : ""
                    }
                  >
                    Apertura/cierre de turno
                  </span>
                </div>

                {/* Caja chica */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.posFeatures.pettyCash ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span
                    className={
                      !limits.posFeatures.pettyCash
                        ? "text-muted-foreground"
                        : ""
                    }
                  >
                    Caja chica
                  </span>
                </div>

                {/* Caja ciega */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.posFeatures.blindBox ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span
                    className={
                      !limits.posFeatures.blindBox
                        ? "text-muted-foreground"
                        : ""
                    }
                  >
                    Caja ciega
                  </span>
                </div>
              </div>

              {/* ===================================================== */}
              {/* CARACTERÍSTICAS GENERALES */}
              {/* ===================================================== */}
              <div className="space-y-3 pt-4 border-t">
                <p className="font-medium text-sm mb-3">Características:</p>

                {/* Inventario - Ahora con niveles */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.modules.inventory === "advanced" ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : limits.modules.inventory === "basic" ? (
                    <Check className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span>
                    Inventario{" "}
                    {limits.modules.inventory === "basic"
                      ? "básico"
                      : "avanzado"}
                  </span>
                </div>

                {/* Módulo Clientes - CORREGIDO */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.modules.customers ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span
                    className={
                      !limits.modules.customers ? "text-muted-foreground" : ""
                    }
                  >
                    Módulo de clientes
                  </span>
                </div>

                {/* Reportes - Ahora con niveles */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.modules.reports !== "none" ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span>
                    {limits.modules.reports === "none" && "Sin reportes"}
                    {limits.modules.reports === "basic" &&
                      "Reportes de ventas, productos, clientes"}
                    {limits.modules.reports === "enterprise" &&
                      "Reportes avanzados + multi-tienda + heatmaps"}
                  </span>
                </div>

                {/* Telegram Bot - CORREGIDO */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.modules.telegram ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span
                    className={
                      !limits.modules.telegram ? "text-muted-foreground" : ""
                    }
                  >
                    Bot de Telegram (alertas básicas)
                  </span>
                </div>

                {/* Soporte - Ahora con niveles */}
                <div className="flex items-center gap-2 text-sm">
                  {limits.support.level !== "basic" ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                  )}
                  <span>
                    {limits.support.level === "basic" && "Soporte básico"}
                    {limits.support.level === "dedicated" &&
                      "Soporte WhatsApp/Telegram dedicado"}
                    {limits.support.level === "premium" &&
                      `Soporte premium ${
                        limits.support.responseTime
                          ? limits.support.responseTime
                          : ""
                      }`}
                  </span>
                </div>

                {/* Onboarding personalizado (solo Enterprise) */}
                {limits.support.onboarding && (
                  <div className="flex items-center gap-2 text-sm pl-6">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Onboarding personalizado</span>
                  </div>
                )}

                {/* Roles disponibles */}
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    Roles disponibles:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {limits.userRoles.map((role) => (
                      <span
                        key={role}
                        className="text-xs bg-gray-100 px-2 py-1 rounded"
                      >
                        {role === "supervisor"
                          ? "Admin"
                          : role === "store_manager"
                            ? "Gerente"
                            : role === "cashier"
                              ? "Cajero"
                              : role === "auditor"
                                ? "Auditor"
                                : role}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="pt-4">
              {!isCurrentPlan && onUpgrade && (
                <Button
                  className="w-full"
                  variant={plan === "enterprise" ? "default" : "outline"}
                  onClick={() => onUpgrade(plan)}
                >
                  {plan === "basic"
                    ? "Plan actual"
                    : `Cambiar a ${PLAN_NAMES[plan]}`}
                </Button>
              )}
              {isCurrentPlan && (
                <Button className="w-full" variant="outline" disabled>
                  Plan actual
                </Button>
              )}
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}
